import React, { useState, useRef } from "react";
import { Activity, Download, Upload } from "lucide-react";

export function SpeedTest({ maxSpeed = 940 }) {
  const [running, setRunning] = useState(false);
  const [ping, setPing] = useState(0);
  const [down, setDown] = useState(0);
  const [up, setUp] = useState(0);
  const [done, setDone] = useState(false);
  const raf = useRef(null);

  const run = () => {
    setRunning(true); setDone(false); setPing(0); setDown(0); setUp(0);
    const targetPing = Math.round(6 + Math.random() * 14);
    const targetDown = Math.round(maxSpeed * (0.75 + Math.random() * 0.2));
    const targetUp = Math.round(targetDown * (0.3 + Math.random() * 0.15));
    const start = performance.now();
    const dur = 2600;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setPing(Math.round(targetPing * Math.min(p * 3, 1)));
      setDown(Math.round(targetDown * ease + (Math.random() * 12 - 6) * (p < 1 ? 1 : 0)));
      setUp(Math.round(targetUp * ease));
      if (p < 1) { raf.current = requestAnimationFrame(tick); }
      else { setDown(targetDown); setUp(targetUp); setRunning(false); setDone(true); }
    };
    raf.current = requestAnimationFrame(tick);
  };

  const pct = Math.min((down / maxSpeed) * 100, 100);

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="speed-test-widget">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2"><Activity size={16} /><span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Live Speed Test</span></div>
        <span className={`h-2 w-2 rounded-full ${running ? "bg-amber-500 animate-pulse" : done ? "bg-emerald-500" : "bg-muted-foreground"}`} />
      </div>

      <div className="relative mx-auto mb-6 h-40 w-40">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle cx="60" cy="60" r="52" fill="none" stroke="hsl(var(--border))" strokeWidth="8" />
          <circle cx="60" cy="60" r="52" fill="none" stroke="hsl(var(--foreground))" strokeWidth="8" strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 52} strokeDashoffset={2 * Math.PI * 52 * (1 - pct / 100)}
            style={{ transition: "stroke-dashoffset 0.1s linear" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-3xl font-bold tabular-nums">{down}</span>
          <span className="font-mono text-xs text-muted-foreground">Mbps ↓</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-5 text-center">
        <div><div className="font-mono text-lg font-bold tabular-nums">{ping}</div><div className="text-xs text-muted-foreground flex items-center justify-center gap-1">ping ms</div></div>
        <div><div className="font-mono text-lg font-bold tabular-nums flex items-center justify-center gap-1"><Download size={13} />{down}</div><div className="text-xs text-muted-foreground">down</div></div>
        <div><div className="font-mono text-lg font-bold tabular-nums flex items-center justify-center gap-1"><Upload size={13} />{up}</div><div className="text-xs text-muted-foreground">up</div></div>
      </div>

      <button onClick={run} disabled={running} data-testid="speed-test-trigger"
        className="w-full h-10 rounded-xl bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
        {running ? "Testing…" : done ? "Run again" : "Run speed test"}
      </button>
    </div>
  );
}
